import React, { FC, useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useClearRefinements } from 'react-instantsearch';
import RefinementEmptyList from './RefinementEmptyList';
import { RefinementDropdown } from './RefinementDropdown';
import { RefinementSearchInput } from '~/features/productList/components/RefinementSearchInput';
import { FilterSelectionHeader } from '~/features/productList/components/FilterSelectionHeader';
import { VirtualizedList } from '~/features/productList/components/VirtualizedList';
import { RefinementProps } from './RefinementProps';
import { Button, SwatchColor } from '~/shared/components';
import Flex from '~/shared/components/Flex';
import Checkmark from '~/icons/checkmark-alt.svg';
import styles from '~/features/productList/components/RefinementListRow/RefinementListRow.module.scss';
import useColorSwatches from '~/features/productList/hooks/useColorSwatches';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { usePinnedRefinementItems } from '~/features/productList/hooks/usePinnedRefinementItems';
import { useDebounce } from 'react-use';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';

type Props = RefinementProps & {
    searchable?: boolean;
    showCount?: boolean;
    placeholder?: string;
};

export const RefinementListColor: FC<Props> = ({
    attribute,
    searchable,
    showCount = true,
    isVisible = true,
    label,
    placeholder,
}) => {
    const { trackFilter } = useDrawerFilterTracking();
    const { colorSwatches } = useColorSwatches();
    const [searchValue, setSearchValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const { refine, searchForItems, allItems } = useOptimizedRefinementList({
        attribute,
        escapeFacetValues: false,
    });
    const pinnedItems = usePinnedRefinementItems(allItems, searchValue, isOpen);
    const { refine: clearSelection } = useClearRefinements({ includedAttributes: [attribute] });
    const selectedCount = allItems.filter((item) => item.isRefined).length;

    const handleRefine = useCallback(
        (value: string) => {
            if (!value) return;

            trackFilter(attribute, value, {
                isRefined: allItems.find((item) => item.value === value)?.isRefined,
            });

            refine(value);
        },
        [attribute, allItems],
    );

    const handleSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.target.value);
    }, []);

    const onClose = useCallback((open: boolean) => {
        setIsOpen(open);
        if (!open) setSearchValue('');
    }, []);

    useDebounce(
        () => {
            searchForItems(searchValue);
        },
        200,
        [searchValue],
    );

    useEffect(() => {
        if (searchValue) searchForItems(searchValue);
    }, [searchValue, pinnedItems]);

    if (!isVisible) return null;

    return (
        <RefinementDropdown
            label={label}
            count={selectedCount}
            searchValue={searchValue}
            onChange={onClose}
            topContent={
                <>
                    {searchable && (
                        <RefinementSearchInput
                            value={searchValue}
                            onChange={handleSearch}
                            onClear={() => setSearchValue('')}
                            placeholder={placeholder}
                        />
                    )}
                    <FilterSelectionHeader
                        label={label ?? ''}
                        selectedCount={selectedCount}
                        onClear={clearSelection}
                    />
                </>
            }
        >
            {allItems.length === 0 ? (
                <RefinementEmptyList />
            ) : (
                <VirtualizedList
                    items={pinnedItems}
                    itemKey={(item) => `${item.label}-${item.value}`}
                    renderItem={({ label: itemLabel, value, isRefined, count }) => (
                        <Flex grow className={styles.dropdownItemContainer}>
                            <Button
                                className={styles.dropdownItemButton}
                                fullWidth
                                variant="plain"
                                onClick={() => handleRefine(value)}
                                tabIndex={-1}
                            >
                                <Flex grow alignItems="center" className={styles.dropdownItemText}>
                                    <Flex className={styles.dropdownTextWrapper}>
                                        <span
                                            className={clsx(styles.checkmarkContainer, {
                                                [styles.isRefined]: isRefined,
                                            })}
                                            role="checkbox"
                                            aria-label={`${itemLabel}, ${count} produkter`}
                                            aria-checked={isRefined}
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key !== ' ' && event.key !== 'Enter')
                                                    return;
                                                event.preventDefault();
                                                handleRefine(value);
                                            }}
                                        >
                                            {isRefined && (
                                                <Checkmark className={styles.checkmark} />
                                            )}
                                        </span>
                                        <SwatchColor
                                            src={
                                                colorSwatches?.find(
                                                    (swatch) => swatch.key === value,
                                                )?.imageUrl
                                            }
                                            alt={itemLabel}
                                        />
                                        {itemLabel}
                                    </Flex>
                                    <span className={styles.dropdownValueWrapper}>
                                        {showCount && count ? count : ''}
                                    </span>
                                </Flex>
                            </Button>
                        </Flex>
                    )}
                />
            )}
        </RefinementDropdown>
    );
};
